
import React, { useState, useEffect, KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send } from 'lucide-react';

interface MessageInputProps {
  onSendMessage: (content: string) => void;
  isSending: boolean;
  disabled?: boolean;
  conversationId?: string;
}

export function MessageInput({ onSendMessage, isSending, disabled, conversationId }: MessageInputProps) {
  const storageKey = conversationId ? `chat_draft_${conversationId}` : null;
  
  const [message, setMessage] = useState(() => {
    if (storageKey) {
      try {
        return sessionStorage.getItem(storageKey) || '';
      } catch { return ''; }
    }
    return '';
  });

  // Persist draft to sessionStorage
  useEffect(() => {
    if (!storageKey) return;
    try {
      if (message) {
        sessionStorage.setItem(storageKey, message);
      } else {
        sessionStorage.removeItem(storageKey);
      }
    } catch {}
  }, [message, storageKey]);

  const handleSend = () => {
    if (message.trim() && !isSending) {
      onSendMessage(message);
      setMessage('');
      if (storageKey) {
        try { sessionStorage.removeItem(storageKey); } catch {}
      }
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t p-4 bg-white">
      <div className="flex space-x-2">
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message..."
          disabled={disabled || isSending}
          className="flex-1 min-h-[40px] max-h-[120px] resize-none"
          rows={1}
        />
        <Button
          onClick={handleSend}
          disabled={!message.trim() || isSending || disabled}
          size="sm"
          className="self-end"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
